// import { useEffect, useState, useMemo, useCallback } from "react";
// import { preload } from "react-dom";

// // Types
// type FetchPriority = "high" | "low" | "auto";
// type CrossOrigin = "anonymous" | "use-credentials";

// interface ImageConfig {
//   url: string;
//   priority: FetchPriority;
//   crossOrigin: CrossOrigin;
//   id?: string;
//   type?: string;
// }

// interface PreloadState {
//   isLoaded: boolean;
//   isLoading: boolean;
//   progress: number;
//   errors: string[];
//   loadedUrls: string[];
//   totalImages: number;
//   loadedCount: number;
// }

// interface UsePreloadImagesOptions {
//   onLoad?: () => void;
//   onError?: (errors: string[]) => void;
//   onProgress?: (progress: number) => void;
// }

// interface UsePreloadImagesReturn extends PreloadState {
//   getImageUrl: (id: string) => string | undefined;
//   getImageById: (id: string) => ImageConfig | undefined;
//   hasError: (url: string) => boolean;
//   retry: () => void;
// }

// export const usePreloadImages = (
//   images: ImageConfig[],
//   options?: UsePreloadImagesOptions
// ): UsePreloadImagesReturn => {
//   const [state, setState] = useState<PreloadState>({
//     isLoaded: false,
//     isLoading: true,
//     progress: 0,
//     errors: [],
//     loadedUrls: [],
//     totalImages: images.length,
//     loadedCount: 0,
//   });
//   const [retryCount, setRetryCount] = useState(0);

//   // memoize images
//   const memoizedImages = useMemo(() => images, [JSON.stringify(images)]);

//   useEffect(() => {
//     // Handle empty images array
//     if (memoizedImages.length === 0) {
//       setState((prev) => ({
//         ...prev,
//         isLoaded: true,
//         isLoading: false,
//         progress: 100,
//       }));
//       options?.onLoad?.();
//       return;
//     }

//     let isMounted = true;
//     let loadedCount = 0;
//     const errors: string[] = [];
//     const loadedUrls: string[] = [];

//     const updateState = (): void => {
//       if (!isMounted) return;

//       const progress = Math.round((loadedCount / memoizedImages.length) * 100);
//       const isLoaded = loadedCount === memoizedImages.length;

//       setState({
//         isLoaded,
//         isLoading: !isLoaded,
//         progress,
//         errors: [...errors],
//         loadedUrls: [...loadedUrls],
//         totalImages: memoizedImages.length,
//         loadedCount,
//       });

//       options?.onProgress?.(progress);

//       if (isLoaded) {
//         options?.onLoad?.();
//         if (errors.length > 0) {
//           options?.onError?.(errors);
//         }
//       }
//     };
//     const handleLoad = (url: string): void => {
//       loadedCount++;
//       loadedUrls.push(url);
//       updateState();
//     };
//     const handleError = (url: string): void => {
//       loadedCount++;
//       errors.push(url);
//       updateState();
//     };

//     // Preload images using React19's preload function
//     memoizedImages.forEach((image) => {
//       preload(image.url, {
//         as: "image",
//         fetchPriority: image.priority,
//         crossOrigin: image.crossOrigin,
//         type: image.type,
//       });

//       // Track loading state with Image object
//       const img = new Image();
//       if (image.crossOrigin) {
//         img.crossOrigin = image.crossOrigin;
//       }
//       img.onload = () => handleLoad(image.url);
//       img.onerror = () => handleError(image.url);
//       img.src = image.url;
//     });

//     return () => {
//       isMounted = false;
//     };
//   }, [memoizedImages, retryCount, options]);

//   // Get image Url by id
//   const getImageUrl = useCallback(
//     (id: string): string | undefined => {
//       return memoizedImages.find((img) => img.id === id)?.url;
//     },
//     [memoizedImages]
//   );
//   // Get full image config by id
//   const getImageById = useCallback(
//     (id: string): ImageConfig | undefined => {
//       return memoizedImages.find((img) => img.id === id);
//     },
//     [memoizedImages]
//   );

//   // Check if url has error
//   const hasError = useCallback(
//     (url: string): boolean => {
//       return state.errors.includes(url);
//     },
//     [state.errors]
//   );
//   //Retry loading images
//   const retry = useCallback((): void => {
//     setState({
//       isLoaded: false,
//       isLoading: true,
//       progress: 0,
//       errors: [],
//       loadedUrls: [],
//       totalImages: memoizedImages.length,
//       loadedCount: 0,
//     });
//     setRetryCount((prev) => prev + 1);
//   }, [memoizedImages.length]);

//   return {
//     ...state,
//     getImageUrl,
//     getImageById,
//     hasError,
//     retry,
//   };
// };

// use case
/**
 *   const images = useMemo(
    () => [
      {
        id: 'hero',
        url: 'https://your-server.com/hero.jpg',
        priority: 'high' as const,
        crossOrigin: 'anonymous' as const,
      },
      {
        id: 'logo',
        url: 'https://your-server.com/logo.png',
        priority: 'high' as const,
        crossOrigin: 'anonymous' as const,
      },
    ],
    []
  );

  const handleLoad = useCallback(() => {
    console.log('All images loaded!');
  }, []);

  const handleError = useCallback((errors: string[]) => {
    console.error('Failed to load:', errors);
  }, []);

  const handleProgress = useCallback((progress: number) => {
    console.log(`Loading: ${progress}%`);
  }, []);

  const {
    isLoaded,
    isLoading,
    progress,
    errors,
    getImageUrl,
    retry,
  } = usePreloadImages(images, {
    onLoad: handleLoad,
    onError: handleError,
    onProgress: handleProgress,
  });

  if (isLoading) {
    return (
      <div className="loading-screen">
        <p>Loading... {progress}%</p>
      </div>
    );
  }

  if (errors.length > 0) {
    return (
      <div className="error-screen">
        <p>Some images failed to load</p>
        <button onClick={retry}>Retry</button>
      </div>
    );
  }

  return (
    <div className="app">
      <img src={getImageUrl('hero')} alt="Hero" />
      <img src={getImageUrl('logo')} alt="Logo" />
    </div>
  );

 */

import { useEffect, useState, useMemo, useCallback } from "react";
import { preload } from "react-dom";

// Types
type FetchPriority = "high" | "low" | "auto";
type CrossOrigin = "anonymous" | "use-credentials";

interface ImageConfig {
  url: string;
  priority: FetchPriority;
  crossOrigin: CrossOrigin;
  id?: string;
  type?: string;
}

interface PreloadState {
  isLoaded: boolean;
  isLoading: boolean;
  progress: number;
  errors: string[];
  loadedUrls: string[];
  totalImages: number;
  loadedCount: number;
}

interface UsePreloadImagesOptions {
  onLoad?: () => void;
  onError?: (errors: string[]) => void;
  onProgress?: (progress: number) => void;
}

interface UsePreloadImagesReturn extends PreloadState {
  getImageUrl: (id: string) => string | undefined;
  getImageById: (id: string) => ImageConfig | undefined;
  hasError: (url: string) => boolean;
  isImageLoaded: (id: string) => boolean; // ADDED
  retry: () => void;
}

export const usePreloadImages = (
  images: ImageConfig[],
  options?: UsePreloadImagesOptions
): UsePreloadImagesReturn => {
  const [state, setState] = useState<PreloadState>({
    isLoaded: false,
    isLoading: true,
    progress: 0,
    errors: [],
    loadedUrls: [],
    totalImages: images.length,
    loadedCount: 0,
  });
  const [retryCount, setRetryCount] = useState(0);

  // Memoize images
  const memoizedImages = useMemo(() => images, [JSON.stringify(images)]);

  useEffect(() => {
    // Handle empty images array
    if (memoizedImages.length === 0) {
      setState((prev) => ({
        ...prev,
        isLoaded: true,
        isLoading: false,
        progress: 100,
      }));
      options?.onLoad?.();
      return;
    }

    let isMounted = true;
    let loadedCount = 0;
    const errors: string[] = [];
    const loadedUrls: string[] = [];

    const updateState = (): void => {
      if (!isMounted) return;

      const progress = Math.round((loadedCount / memoizedImages.length) * 100);
      const isLoaded = loadedCount === memoizedImages.length;

      setState({
        isLoaded,
        isLoading: !isLoaded,
        progress,
        errors: [...errors],
        loadedUrls: [...loadedUrls],
        totalImages: memoizedImages.length,
        loadedCount,
      });

      options?.onProgress?.(progress);

      if (isLoaded) {
        options?.onLoad?.();
        if (errors.length > 0) {
          options?.onError?.(errors);
        }
      }
    };

    const handleLoad = (url: string): void => {
      loadedCount++;
      loadedUrls.push(url);
      updateState();
    };

    const handleError = (url: string): void => {
      loadedCount++;
      errors.push(url);
      updateState();
    };

    // Preload images using React 19's preload function
    memoizedImages.forEach((image) => {
      preload(image.url, {
        as: "image",
        fetchPriority: image.priority,
        crossOrigin: image.crossOrigin,
        type: image.type,
      });

      // Track loading state with Image object
      const img = new Image();
      if (image.crossOrigin) {
        img.crossOrigin = image.crossOrigin;
      }
      img.onload = () => handleLoad(image.url);
      img.onerror = () => handleError(image.url);
      img.src = image.url;
    });

    return () => {
      isMounted = false;
    };
  }, [memoizedImages, retryCount, options]);

  // ✅ FIXED: Get image URL by id - only if loaded
  const getImageUrl = useCallback(
    (id: string): string | undefined => {
      const image = memoizedImages.find((img) => img.id === id);
      if (!image) return undefined;

      // Only return URL if it's in loadedUrls
      if (state.loadedUrls.includes(image.url)) {
        return image.url;
      }

      return undefined;
    },
    [memoizedImages, state.loadedUrls]
  );

  // Get full image config by id
  const getImageById = useCallback(
    (id: string): ImageConfig | undefined => {
      return memoizedImages.find((img) => img.id === id);
    },
    [memoizedImages]
  );

  // ✅ ADDED: Check if specific image is loaded by id
  const isImageLoaded = useCallback(
    (id: string): boolean => {
      const image = memoizedImages.find((img) => img.id === id);
      if (!image) return false;
      return state.loadedUrls.includes(image.url);
    },
    [memoizedImages, state.loadedUrls]
  );

  // Check if url has error
  const hasError = useCallback(
    (url: string): boolean => {
      return state.errors.includes(url);
    },
    [state.errors]
  );

  // Retry loading images
  const retry = useCallback((): void => {
    setState({
      isLoaded: false,
      isLoading: true,
      progress: 0,
      errors: [],
      loadedUrls: [],
      totalImages: memoizedImages.length,
      loadedCount: 0,
    });
    setRetryCount((prev) => prev + 1);
  }, [memoizedImages.length]);

  return {
    ...state,
    getImageUrl,
    getImageById,
    hasError,
    isImageLoaded, // ADDED
    retry,
  };
};
